import HelpCenterLayout from "@components/helpCenter/HelpCenterLayout"
import {
  AccessUntil,
  ResultView,
  StoreLinks,
} from "@components/helpCenter/ResultParts"
import { resultTextComponents } from "@components/helpCenter/resultTextComponents"
import { decideRefundResult } from "@helpers/helpCenterResult"
import { RefundType } from "@pages/helpCenter/RefundPage"
import { Trans, useTranslation } from "react-i18next"
import { Navigate, useLocation } from "react-router-dom"
import { RefundRevenueCatSubscriptionsData } from "src/types"

export type RefundCompletedPageLocationState =
  Partial<RefundRevenueCatSubscriptionsData> & {
    /** ISO date the canceled web subscriptions' Pro access ends, if known. */
    accessEndDate?: string | null
    refundType: RefundType
  }

export default function RefundCompletedPage() {
  const { t } = useTranslation()
  const location = useLocation()

  const state = location.state as RefundCompletedPageLocationState | null

  // NOTE: Opened directly (e.g. reloaded after the state was lost): there's
  // no outcome to report.
  if (!state?.refundType) return <Navigate replace to="/help-center" />

  const { accessEndDate, refundType } = state

  const { hasStoreSubscription, layout, storeInstructions, web } =
    decideRefundResult(state, refundType)

  const webMessage = () => {
    switch (web) {
      case "refunded":
        return (
          <p>
            <Trans
              components={resultTextComponents}
              i18nKey={
                hasStoreSubscription
                  ? "pages.helpCenter.refundCompleted.webSubscriptionCanceledAndRefunded"
                  : "pages.helpCenter.refundCompleted.subscriptionSuccessfullyCanceledAndRefunded"
              }
            />
          </p>
        )

      case "canceledNotRefunded":
        return (
          <>
            <p>
              <Trans
                components={resultTextComponents}
                i18nKey="pages.helpCenter.refundCompleted.subscriptionCouldNotBeRefunded"
              />
            </p>

            <AccessUntil date={accessEndDate} />
          </>
        )

      case "notConfirmed":
        return (
          <p>
            <Trans
              components={resultTextComponents}
              i18nKey="pages.helpCenter.refundCompleted.refundNotConfirmed"
            />
          </p>
        )

      case "noWebSubscription":
        // NOTE: Next to store instructions, the user simply has no web
        // subscription: nothing to report about it.
        if (storeInstructions) return null

        return (
          <p>
            <Trans
              components={resultTextComponents}
              i18nKey="pages.helpCenter.refundCompleted.noWebSubscriptionToRefund"
            />
          </p>
        )
    }
  }

  if (!storeInstructions)
    return (
      <HelpCenterLayout>
        <ResultView
          icon={layout === "success" ? "success" : "warning"}
          title={
            layout === "success"
              ? t("pages.helpCenter.thankYou")
              : t("pages.helpCenter.weAreSorry")
          }
        >
          {webMessage()}
        </ResultView>
      </HelpCenterLayout>
    )

  const { apple, google } = storeInstructions

  const regardingSubscriptionTranslation = () => {
    if (apple && google)
      return t("pages.helpCenter.regardingGoogleAndAppleSubscriptions")

    if (google) return t("pages.helpCenter.regardingGoogleSubscription")

    return t("pages.helpCenter.regardingAppleSubscription")
  }

  const followGuidelinesTranslation = () => {
    if (apple && google)
      return t(
        "pages.helpCenter.refundCompleted.followGoogleAndAppleGuidelines",
      )

    if (apple)
      return t("pages.helpCenter.refundCompleted.followAppleGuidelines")

    return t("pages.helpCenter.refundCompleted.followGoogleGuidelines")
  }

  return (
    <HelpCenterLayout>
      <ResultView
        icon="warning"
        title={t("pages.helpCenter.shared.furtherActionNeeded")}
      >
        {webMessage()}

        <p className="font-semibold text-vm-ink">
          {regardingSubscriptionTranslation()}
        </p>

        <p>
          {followGuidelinesTranslation()}{" "}
          <span className="font-semibold text-vm-ink">
            {t("pages.helpCenter.refundCompleted.followInstructionsToRefund")}
          </span>
        </p>

        <StoreLinks apple={apple} google={google} />
      </ResultView>
    </HelpCenterLayout>
  )
}
